import { Box, SxProps, useMediaQuery, useTheme } from "@mui/material";
import { Button } from "@powerdiary/components-library/lib";
import { ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";
import TermsAndConditionsLink from "../../../Shared/TermsAndConditionsLink";
import TermsAndConditionsContent from "../../../Shared/TermsAndConditionsContent";
import { ConfirmBookingFunction } from "../../../../hooks/useBookingConfirmation";
import { useValidateBooking } from "../../../../hooks/useValidateBooking";
import { useSingleFlightSubmit } from "../../../../hooks/useSingleFlightSubmit";
import { useClientPortalSelector } from "../../../../../../redux";
import { selectBooking } from "../../../../bookingSlice";

interface ConfirmationSubmitProps {
    confirmBooking: ConfirmBookingFunction;
    isLoading: boolean;
}

export default function ConfirmationSubmit({ confirmBooking, isLoading }: Readonly<ConfirmationSubmitProps>) {
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const { session } = useClientPortalSelector(selectBooking);

    const bookingType = session.bookingType;
    const [termsAndConditionsVisibility, setTermsAndConditionsVisibility] = useState<boolean>(false);
    const { validateBooking } = useValidateBooking();
    const { isSubmitting, runExclusive } = useSingleFlightSubmit();

    const bookingClientLog = `Booking from client portal - ${bookingType.toLowerCase()}`;

    // The guard has to span validation as well as the booking itself: isLoading only covers
    // the Schedule mutation, so without it the button stays live for the whole ValidateBooking
    // round-trip and a second click books a duplicate appointment.
    const handleConfirmBooking = () =>
        runExclusive(async () => {
            try {
                await validateBooking();
            } catch {
                // Validation errors are surfaced by useValidateBooking; stop the booking flow.
                return;
            }
            await confirmBooking(bookingClientLog);
        });

    const wrapperSx: SxProps = useMemo(
        () => ({
            display: "flex",
            flexDirection: isTablet ? "column" : "row",
            gap: "8px",
            alignSelf: isTablet ? "center" : "flex-end",
        }),
        [isTablet]
    );

    const termsAndConditionsLinkSx: SxProps = useMemo(
        () => ({
            minHeight: "35px",
            display: "flex",
            flexDirection: isTablet ? "column" : "row",
            gap: "4px",
            alignItems: "center",
        }),
        [isTablet]
    );

    return (
        <>
            <Box sx={wrapperSx} data-test-id="section_confirmation_submit">
                <TermsAndConditionsLink
                    wrapperSx={termsAndConditionsLinkSx}
                    termsAndConditionsVisibility={termsAndConditionsVisibility}
                    setTermsAndConditionsVisibility={setTermsAndConditionsVisibility}
                />
                <Button
                    variant="contained"
                    color="primary"
                    size="extraSmall"
                    endIcon={<ArrowRight size={14} strokeWidth={2} aria-hidden />}
                    onClick={() => void handleConfirmBooking()}
                    disabled={isSubmitting || isLoading}
                    data-test-id="button_confirm_booking"
                >
                    {isSubmitting || isLoading ? "CONFIRMING..." : "CONFIRM BOOKING"}
                </Button>
            </Box>
            <TermsAndConditionsContent termsAndConditionsVisibility={termsAndConditionsVisibility} />
        </>
    );
}
